import { Box, Tooltip, Typography } from "@mui/joy";
import moment from "moment-timezone";
import { typeOptions } from "../../constants";
import { Contractor } from "../../entities/contractor";
import { Gig } from "../../entities/gig";
import { Project } from "../../entities/project";
import { Stage } from "../../entities/stage";
import { Venue } from "../../entities/venue";

const appleRed = "#FF3B30";

/**
 * Relative countdown wording for an upcoming service.
 *
 * @param gig
 * @param tz
 */
function relativeLabel(gig: Gig, tz?: string): string | undefined {
  if (gig.toBeDetermined() || gig.completed(tz)) return undefined;
  // Bucket both the service date and "now" in the same (venue) zone so the
  // day delta can't drift by one near midnight for cross-timezone viewers.
  const days = gig
    .from(tz)
    .clone()
    .startOf("day")
    .diff(moment.tz(gig.resolvedTz(tz)).startOf("day"), "days");
  if (days <= 0) return "Today";
  if (days === 1) return "Tomorrow";
  return `In ${days} days`;
}

/**
 * Apple-style card for a single service (gig).
 *
 * @param root0
 * @param root0.gig
 * @param root0.venue
 * @param root0.contractor
 * @param root0.project
 * @param root0.stage
 * @param root0.index
 * @param root0.onClick
 * @returns {ReactElement} ServiceCard
 */
export function ServiceCard({
  gig,
  venue,
  contractor,
  project,
  stage,
  index = 0,
  onClick,
}: {
  gig: Gig;
  venue?: Venue;
  contractor?: Contractor;
  project?: Project;
  stage?: Stage;
  index?: number;
  onClick: (gig: Gig) => void;
}) {
  const tz = venue?.tzName;
  const tbd = gig.toBeDetermined();
  const past = gig.completed(tz);
  const today = gig.happeningToday(tz);
  const countdown = relativeLabel(gig, tz);
  const locked = gig.sourceID !== 420;
  const playing = gig.isPlaying();
  const stageColor = stage?.color || "#8E8E93";
  const showStage = stage && gig.stageID > 0 && gig.isPlaying();
  const sessionType = gig.sessionType();
  const sessionTypeIcon =
    typeOptions.find((t) => t.sessionTypeID === gig.sessionTypeID)?.icon ??
    "fa-solid fa-music";
  const cover = project?.coverURL;

  return (
    <Box
      onClick={() => onClick(gig)}
      sx={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 2,
        p: 2,
        background: "var(--mui-palette-background-paper, #fff)",
        borderRadius: "16px",
        border: "1px solid var(--mui-palette-divider)",
        boxShadow:
          "0 1px 2px rgba(0, 0, 0, 0.03), 0 4px 12px rgba(0, 0, 0, 0.03)",
        cursor: "pointer",
        transition: "all 0.25s cubic-bezier(0.3, 0, 0.2, 1)",
        animation: "cardIn 0.3s ease both",
        animationDelay: `${Math.min(index, 12) * 0.03}s`,
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow:
            "0 2px 4px rgba(0, 0, 0, 0.04), 0 12px 28px rgba(0, 0, 0, 0.09)",
          borderColor: "rgba(var(--mui-palette-primary-mainChannel) / 0.35)",
        },
        "&:active": {
          transform: "scale(0.99)",
          boxShadow: "0 1px 2px rgba(0, 0, 0, 0.03)",
        },
      }}
    >
      {/* Leading visual: the project cover (dominant) with the date on it,
          or a calendar date tile when there's no cover. Countdown beneath. */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          flexShrink: 0,
          gap: 0.5,
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: 64,
            height: 64,
            borderRadius: "14px",
            overflow: "hidden",
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: cover
              ? "#0b0b0c"
              : today
              ? "rgba(255, 59, 48, 0.09)"
              : "rgba(120, 120, 128, 0.09)",
          }}
        >
          {cover ? (
            <Box
              component="img"
              src={cover}
              sx={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          ) : (
            []
          )}
          {tbd ? (
            !cover ? (
              <Typography
                sx={{
                  fontSize: 22,
                  color: "var(--mui-palette-text-tertiary)",
                }}
              >
                <i className="fa-solid fa-question"></i>
              </Typography>
            ) : (
              []
            )
          ) : cover ? (
            <>
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  background:
                    "linear-gradient(180deg, rgba(0,0,0,0) 38%, rgba(0,0,0,0.55) 100%)",
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  bottom: 4,
                  left: 0,
                  right: 0,
                  textAlign: "center",
                  lineHeight: 1,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.5625rem",
                    fontWeight: 700,
                    letterSpacing: "0.05em",
                    lineHeight: 1.2,
                    color: "#fff",
                    textShadow:
                      "0 1px 2px rgba(0,0,0,0.8), 0 0 3px rgba(0,0,0,0.6)",
                  }}
                >
                  {gig.from(tz).format("ddd").toUpperCase()}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "1.0625rem",
                    fontWeight: 700,
                    lineHeight: 1.05,
                    color: "#fff",
                    textShadow:
                      "0 1px 2px rgba(0,0,0,0.8), 0 0 3px rgba(0,0,0,0.6)",
                  }}
                >
                  {gig.from(tz).format("D")}
                </Typography>
              </Box>
            </>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                lineHeight: 1,
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.6875rem",
                  fontWeight: 700,
                  letterSpacing: "0.06em",
                  lineHeight: 1.2,
                  color: past ? "var(--mui-palette-text-tertiary)" : appleRed,
                }}
              >
                {gig.from(tz).format("ddd").toUpperCase()}
              </Typography>
              <Typography
                sx={{
                  fontSize: "1.5rem",
                  fontWeight: 600,
                  lineHeight: 1.1,
                  color: past
                    ? "var(--mui-palette-text-tertiary)"
                    : "var(--mui-palette-text-primary)",
                }}
              >
                {gig.from(tz).format("D")}
              </Typography>
            </Box>
          )}
        </Box>
        {countdown ? (
          <Typography
            sx={{
              fontSize: "0.6875rem",
              fontWeight: 600,
              textAlign: "center",
              lineHeight: 1.15,
              maxWidth: 72,
              color: today ? appleRed : "var(--mui-palette-text-secondary)",
            }}
          >
            {countdown}
          </Typography>
        ) : (
          []
        )}
      </Box>

      {/* Title, project, time & venue */}
      <Box
        sx={{
          flex: 1,
          minWidth: 220,
          display: "flex",
          flexDirection: "column",
          gap: 0.25,
        }}
      >
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}
        >
          {locked ? (
            <Tooltip
              arrow
              size="sm"
              variant="outlined"
              sx={{ maxWidth: 200, textAlign: "center" }}
              title="This item cannot be edited. It is controlled by an organization using Rhapsody."
            >
              <Typography
                sx={{
                  fontSize: 11,
                  flexShrink: 0,
                  color: "var(--mui-palette-text-tertiary)",
                }}
              >
                <i className="fa-solid fa-lock"></i>
              </Typography>
            </Tooltip>
          ) : (
            []
          )}
          <Typography
            level="body1"
            sx={{
              fontWeight: 600,
              fontSize: "0.9375rem",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              minWidth: 0,
            }}
          >
            {gig.title ?? "Untitled Service"}
          </Typography>
          <Typography
            sx={{
              flexShrink: 0,
              fontSize: "0.6875rem",
              fontWeight: 600,
              whiteSpace: "nowrap",
              // Playing → plain green text; Not playing → solid red pill.
              ...(playing
                ? {
                    color: "#1B7A33",
                    '[data-joy-color-scheme="dark"] &, [data-mui-color-scheme="dark"] &':
                      { color: "#5DE27E" },
                  }
                : {
                    color: "#fff",
                    background: "#C9251C",
                    px: 0.75,
                    py: 0.125,
                    borderRadius: "999px",
                  }),
            }}
          >
            {playing ? "Playing" : "Not playing"}
          </Typography>
        </Box>
        {project ? (
          <Typography
            level="body3"
            noWrap
            startDecorator={
              <i className="fa-regular fa-folder" style={{ fontSize: 11 }}></i>
            }
            sx={{
              color: "var(--mui-palette-text-secondary)",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {project.name}
          </Typography>
        ) : (
          []
        )}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            columnGap: 1.5,
            rowGap: 0.25,
            flexWrap: "wrap",
            mt: 0.25,
          }}
        >
          {sessionType ? (
            <Typography
              level="body3"
              sx={{
                color: "var(--mui-palette-text-secondary)",
                fontWeight: 600,
              }}
              startDecorator={
                <i className={sessionTypeIcon} style={{ fontSize: 11 }}></i>
              }
            >
              {sessionType}
            </Typography>
          ) : (
            []
          )}
          {!tbd && !gig.noTime(tz) ? (
            <Typography
              level="body3"
              sx={{ color: "var(--mui-palette-text-secondary)" }}
              startDecorator={
                <i className="fa-regular fa-clock" style={{ fontSize: 11 }}></i>
              }
            >
              {gig.startTime(tz)} – {gig.endTime(tz)} {gig.from(tz).format("z")}
            </Typography>
          ) : (
            []
          )}
          <Typography
            level="body3"
            sx={{
              color: "var(--mui-palette-text-secondary)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            startDecorator={
              <i
                className="fa-solid fa-location-dot"
                style={{ fontSize: 11 }}
              ></i>
            }
          >
            {gig.venueID === -1
              ? "Venue to be determined"
              : venue
              ? `${venue.name} · ${venue.city}, ${venue.state}`
              : "Unknown venue"}
          </Typography>
        </Box>
      </Box>

      {/* Organization, status & countdown */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          justifyContent: "center",
          gap: 0.75,
          ml: "auto",
          flexShrink: 0,
        }}
      >
        {contractor?.coverURL ? (
          <Tooltip size="sm" arrow title={contractor?.name}>
            <Box
              sx={{
                background: contractor.color,
                borderRadius: "8px",
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <img
                src={contractor.coverURL}
                height={26}
                style={{
                  maxWidth: 130,
                  objectFit: "contain",
                  display: "block",
                }}
              />
            </Box>
          </Tooltip>
        ) : contractor?.name ? (
          <Typography
            level="body3"
            sx={{ fontWeight: 600, maxWidth: 150 }}
            noWrap
          >
            {contractor.name}
          </Typography>
        ) : (
          []
        )}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          {showStage ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                px: 1,
                py: 0.25,
                borderRadius: "999px",
                background: `${stageColor}1A`,
              }}
            >
              {gig.isCattleCall() ? (
                <i
                  className="fa-solid fa-cowbell fa-shake"
                  style={{ fontSize: 12, color: stageColor }}
                />
              ) : (
                <img src={`${stage?.icon}`} style={{ height: 12 }} />
              )}
              <Typography
                sx={{
                  color: stageColor,
                  fontWeight: 600,
                  fontSize: "0.75rem",
                }}
              >
                {stage?.wording()}
              </Typography>
            </Box>
          ) : (
            []
          )}
        </Box>
      </Box>
      <Typography
        sx={{
          fontSize: 12,
          color: "var(--mui-palette-text-tertiary)",
          display: { xs: "none", sm: "block" },
        }}
      >
        <i className="fa-solid fa-chevron-right"></i>
      </Typography>
    </Box>
  );
}
